// Service-originated attach flow. Unlike attach-persistent-disk-modal
// (disk -> service: pick a service for a known disk), this modal is rooted on a
// service and lets the user EITHER create a brand-new disk and attach it OR
// attach an existing disk from the environment's disk pool. Create-new composes
// createAndAttachPersistentDisk; attach-existing reuses attachPersistentDisk.
import {
  attachPersistentDisk,
  createAndAttachPersistentDisk,
  selectActiveMountPointsByServiceId,
  selectDiskAttachmentsAsList,
  selectPersistentDisksByEnvId,
} from "@app/deploy";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import { environmentActivityUrl } from "@app/routes";
import type { WebState } from "@app/schema";
import type { DeployService } from "@app/types";
import { useValidator } from "@app/ui/hooks";
import { Banner, BannerMessages } from "@app/ui/shared/banner";
import { Button } from "@app/ui/shared/button";
import { ExternalLink } from "@app/ui/shared/external-link";
import { Form, FormGroup } from "@app/ui/shared/form-group";
import { IconCylinder } from "@app/ui/shared/icons";
import { Input } from "@app/ui/shared/input";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import {
  Radio,
  RadioGroup,
  Select,
  type SelectOption,
} from "@app/ui/shared/select";
import { handleValidator } from "@app/validator";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { normalizeMountPoint } from "./attach-persistent-disk-modal";
import { DiskRestartWarning } from "./disk-restart-warning";

type DiskSource = "new" | "existing";

interface AttachToServiceFormData {
  source: DiskSource;
  handle: string;
  size: number;
  existingDiskId: string;
  mountPoint: string;
  // Mount points already in use on this service (active attachments),
  // lower-cased & trimmed for case-insensitive comparison.
  existingMountPoints: string[];
}

const validators = {
  handle: (data: AttachToServiceFormData) => {
    if (data.source !== "new") return;
    return handleValidator(data.handle);
  },
  size: (data: AttachToServiceFormData) => {
    if (data.source !== "new") return;
    if (!Number.isFinite(data.size) || data.size <= 0) {
      return "Size must be a positive number of GB";
    }
  },
  existingDiskId: (data: AttachToServiceFormData) => {
    if (data.source !== "existing") return;
    if (!data.existingDiskId) return "Choose a disk to attach";
  },
  mountPoint: (data: AttachToServiceFormData) => {
    const mountPoint = normalizeMountPoint(data.mountPoint);
    if (!mountPoint) return "Mount path is required";
    if (!mountPoint.startsWith("/")) {
      return "Mount path must be an absolute path (e.g., /data)";
    }
    if (data.existingMountPoints.includes(mountPoint.toLowerCase())) {
      return "That mount path is already in use on this service";
    }
  },
};

interface AttachDiskToServiceModalProps {
  isOpen: boolean;
  service: DeployService;
  onClose: () => void;
}

export const AttachDiskToServiceModal = ({
  isOpen,
  service,
  onClose,
}: AttachDiskToServiceModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [source, setSource] = useState<DiskSource>("new");
  const [handle, setHandle] = useState("");
  // Size is stored as a raw string so the user can clear the field without it
  // reverting to 0, and the validator surfaces an error for blank/invalid
  // input. The numeric value is derived for submit + form-data.
  const [sizeInput, setSizeInput] = useState("10");
  const size = sizeInput.trim() === "" ? Number.NaN : Number(sizeInput);
  const [existingDiskId, setExistingDiskId] = useState("");
  const [mountPoint, setMountPoint] = useState("");

  // Existing-disk pool is scoped to this service's environment.
  const disks = useSelector((s) =>
    selectPersistentDisksByEnvId(s, { envId: service.environmentId }),
  );
  const allAttachments = useSelector(selectDiskAttachmentsAsList);
  // Only provisioned disks can be attached, and a disk has at most one active
  // attachment; paths change via detach -> re-attach. So a disk with ANY active
  // (non-deprovisioned) attachment is not attachable anywhere and is excluded
  // from the pool. A detached attachment frees the disk to be offered again.
  const attachableDisks = useMemo(
    () =>
      disks.filter((d) => {
        if (d.status !== "provisioned") return false;
        return !allAttachments.some(
          (a) => a.persistentDiskId === d.id && a.status !== "deprovisioned",
        );
      }),
    [disks, allAttachments],
  );

  // Active attachments on this service back the mount-point uniqueness check.
  // Detached (deprovisioned) attachments free up their mount point.
  const existingMountPoints = useSelector((s: WebState) =>
    selectActiveMountPointsByServiceId(s, { serviceId: service.id }),
  );

  const [errors, validate] = useValidator<
    AttachToServiceFormData,
    typeof validators
  >(validators);

  // Both flows share a loader surface; the active one drives the button state.
  const createLoader = useLoader(createAndAttachPersistentDisk);
  const attachLoader = useLoader(attachPersistentDisk);
  const loader = source === "new" ? createLoader : attachLoader;

  const handleClose = () => {
    setSource("new");
    setHandle("");
    setSizeInput("10");
    setExistingDiskId("");
    setMountPoint("");
    onClose();
  };

  useLoaderSuccess(loader, () => {
    // TODO: use appActivityUrl once operations properly populate targetResources
    // with the service and app being attached to.
    navigate(environmentActivityUrl(service.environmentId));
  });

  const containerCount = service.containerCount;
  const tooManyContainers = containerCount > 1;
  const tooManyContainersWarning = tooManyContainers
    ? `${service.processType} is running ${containerCount} containers. Scale down to 1 or fewer to attach a disk.`
    : "";

  const diskOptions: SelectOption[] = [
    { label: "Select a disk", value: "" },
    ...attachableDisks.map((d) => ({
      label: `${d.handle} (${d.size} GB)`,
      value: d.id,
    })),
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: AttachToServiceFormData = {
      source,
      handle,
      size,
      existingDiskId,
      mountPoint,
      existingMountPoints,
    };
    if (!validate(data)) return;

    if (source === "new") {
      dispatch(
        createAndAttachPersistentDisk({
          envId: service.environmentId,
          handle,
          size,
          serviceId: service.id,
          mountPoint: normalizeMountPoint(mountPoint),
        }),
      );
    } else {
      dispatch(
        attachPersistentDisk({
          id: existingDiskId,
          serviceId: service.id,
          mountPoint: normalizeMountPoint(mountPoint),
        }),
      );
    }
  };

  if (!isOpen) return null;

  const requiredFieldsFilled =
    source === "new"
      ? Boolean(handle.trim()) && Number.isFinite(size) && size > 0
      : Boolean(existingDiskId);
  const submitDisabled =
    !requiredFieldsFilled || !mountPoint.trim() || tooManyContainers;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={handleClose} className="min-w-[600px]">
        <ModalHeading
          icon={<IconCylinder variant="sm" />}
          title="Attach Persistent Disk"
          onClose={handleClose}
        />
        <Form onSubmit={handleSubmit}>
          <ModalBody className="pb-0">
            <DiskRestartWarning className="mb-4" />

            <RadioGroup
              name="attach-disk-source"
              selected={source}
              onSelect={(v) => setSource(v as DiskSource)}
              orientation="horizontal"
              className="gap-6 mb-4"
              aria-label="Disk source"
            >
              <Radio value="new">Create new disk</Radio>
              <Radio value="existing">Attach existing disk</Radio>
            </RadioGroup>

            {tooManyContainersWarning ? (
              <Banner variant="error" className="mb-4">
                {tooManyContainersWarning}
              </Banner>
            ) : null}

            {source === "new" ? (
              <FormGroup
                label={
                  <>
                    Handle <span className="text-red-500">*</span>
                  </>
                }
                htmlFor="attach-disk-handle"
                className="mb-4"
                feedbackMessage={errors.handle}
                feedbackVariant={errors.handle ? "danger" : "default"}
              >
                <Input
                  id="attach-disk-handle"
                  name="handle"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder="e.g., shared-uploads"
                  maxLength={64}
                />
              </FormGroup>
            ) : attachableDisks.length === 0 ? (
              <Banner variant="warning" className="mb-4">
                No detached disks available. Detach an existing disk or create a
                new disk.
              </Banner>
            ) : (
              <FormGroup
                label={
                  <>
                    Choose Disk <span className="text-red-500">*</span>
                  </>
                }
                htmlFor="attach-disk-existing"
                className="mb-4"
                feedbackMessage={errors.existingDiskId}
                feedbackVariant={errors.existingDiskId ? "danger" : "default"}
              >
                <Select
                  id="attach-disk-existing"
                  ariaLabel="disk"
                  options={diskOptions}
                  value={existingDiskId}
                  onSelect={(opt) => setExistingDiskId(opt.value)}
                />
              </FormGroup>
            )}

            <FormGroup
              label={
                <>
                  Mount Path <span className="text-red-500">*</span>
                </>
              }
              htmlFor="attach-disk-mount-path"
              className="mb-4"
              feedbackMessage={errors.mountPoint}
              feedbackVariant={errors.mountPoint ? "danger" : "default"}
            >
              <Input
                id="attach-disk-mount-path"
                name="mountPoint"
                value={mountPoint}
                onChange={(e) => setMountPoint(e.target.value)}
                placeholder="/data"
                maxLength={255}
              />
              <p className="mt-1 text-xs text-gray-500">
                <ExternalLink
                  href="https://www.aptible.com/docs/core-concepts/apps/persistent-disks"
                  className="hover:underline"
                >
                  Learn more about mount paths
                </ExternalLink>
              </p>
            </FormGroup>

            {source === "new" ? (
              <FormGroup
                label={
                  <>
                    Size (GB) <span className="text-red-500">*</span>
                  </>
                }
                htmlFor="attach-disk-size"
                className="mb-4"
                feedbackMessage={errors.size}
                feedbackVariant={errors.size ? "danger" : "default"}
              >
                <Input
                  id="attach-disk-size"
                  name="size"
                  type="number"
                  min={1}
                  value={sizeInput}
                  onChange={(e) => setSizeInput(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  You can increase disk size at any time, but cannot decrease
                  it.
                </p>
              </FormGroup>
            ) : null}

            <BannerMessages className="mt-4" {...loader} />
          </ModalBody>
          <ModalFooter
            actions={[
              <Button
                key="submit"
                type="submit"
                disabled={submitDisabled}
                isLoading={loader.isLoading}
              >
                Attach Disk
              </Button>,
              <Button
                key="cancel"
                variant="white"
                onClick={handleClose}
                type="button"
              >
                Cancel
              </Button>,
            ]}
          />
        </Form>
      </Modal>
    </ModalWrapper>
  );
};
